import type { InternalFeaturesConfig } from './types';

/**
 * Baseline feature flags merged with external `features` during normalization.
 * Only truly configurable defaults live here — derived flags are computed in
 * normalizeFeatures() from schema structure, never spelled out as defaults.
 */
export const FEATURES_DEFAULTS: InternalFeaturesConfig = {
  // Column preferences: null = feature disabled (no picker, no persistence).
  // Derived from presence of `columnPreferences.persistenceKey` + optional columns.
  columnPreferences: null,

  pagination: {
    defaultSize: 25,
    options: [10, 25, 50, 100],
    mode: 'pagination',
  },

  // Selection: all derived flags default to "off"; set by normalizer from schema structure.
  selection: {
    enabled: false,
    keyField: '',
    showCheckbox: false,
    multiSelect: false,
    selectAllPages: false,
    bulkActionsEnabled: false,
    selectable: null,
  },

  search: {
    enabled: false,
    debounce: 300,
    placeholder: 'Search…',
  },

  editing: { bulkSave: false, externalControl: false },
  groupBy: null,
  rows: {
    height: 40,
    maxHeight: null,
    striped: false,
    subRows: null,
  },
  virtualScroll: {
    enabled: false,
    estimatedRowHeight: 47,
    overscan: 12,
    threshold: 100,
    pageSize: 50,
    maxCachedPages: 10,
    fetchDebounce: 500,
  },
  rowPinning: null,
  filterGrid: { columns: 2 },
  layout: { noPadding: false },
  footer: null,
};

/** Used when normalizing column `width` from a bare number or partial object. */
export const COLUMN_WIDTH_DEFAULTS = {
  min: 80,
  max: 2000,
  default: 160,
} as const;

/**
 * Semantic default widths by column type — used when the schema author omits `width`.
 * Chosen to reflect realistic content sizes so the table looks right out of the box.
 */
export const COLUMN_TYPE_WIDTHS: Record<string, { min: number; default: number; max: number }> = {
  // Short status labels / badges → compact
  badge:            { min: 100, default: 150, max: 220 },
  // Boolean badges (Yes/No, Sent/Not Sent) → very compact
  conditional:      { min:  80, default: 120, max: 180 },
  // Name + subtitle stacked → wider
  couple:           { min: 160, default: 220, max: 360 },
  coupleWithDateRange: { min: 200, default: 260, max: 400 },
  // Date / time → fixed narrow
  date:             { min: 110, default: 130, max: 160 },
  dateTime:         { min: 150, default: 180, max: 220 },
  dateTimeLTS:      { min: 160, default: 200, max: 240 },
  dateRange:        { min: 200, default: 240, max: 320 },
  conditionalDate:  { min: 110, default: 140, max: 180 },
  time:             { min:  90, default: 110, max: 150 },
  // Numbers → medium-narrow
  number:           { min:  90, default: 120, max: 180 },
  currency:         { min: 100, default: 130, max: 200 },
  percentage:       { min:  80, default: 110, max: 160 },
  // Links, modals, masks — treat like text
  link:             { min: 120, default: 160, max: 300 },
  modal:            { min: 120, default: 160, max: 300 },
  mask:             { min: 120, default: 160, max: 260 },
  sensitiveReveal:  { min: 140, default: 180, max: 280 },
  // Arrays → medium
  arraySplit:       { min: 120, default: 180, max: 300 },
  // Generic / fallback
  text:             { min:  80, default: 160, max: 400 },
  html:             { min: 120, default: 200, max: 500 },
  space:            { min:  40, default:  40, max:  40 },
  custom:           { min:  80, default: 160, max: 400 },
};
